import {
  MaybeEmploymentSummaryInfo,
  MaybeMembershipSummaryInfo,
  MaybeUserInfo
} from '../../types/types'
import { isArrayWithLength } from '../../utils'
import {
  useConditionalText,
  useTextOrTranslation,
  useTranslation
} from '../translations'
import { FormattedUserData, FormattedUserDataKey } from './types'
import { useLatestLogin } from './useLatestLogin'
import {
  filterValidEmployment,
  formatContactList,
  formatUserName
} from './utils'

/**
 * Imperative way of including formatMessage of useIntl for
 * @param user
 * @returns contacts with translated N/A (null/undefined) ones.
 */
export const useFormattedUserData = (
  user: MaybeUserInfo,
  employment: MaybeEmploymentSummaryInfo,
  membership: MaybeMembershipSummaryInfo
) => {
  const userAddressList = user?.yhteystiedot?.osoitteet || []
  const userPhoneList = user?.yhteystiedot?.puhelinnumerot || []
  const userEmailList = user?.yhteystiedot?.sahkopostit || []

  const contactInfoList = formatContactList(
    userAddressList,
    userPhoneList,
    userEmailList
  )

  const userNames = formatUserName(
    user?.nimi?.etunimet || '',
    user?.nimi?.sukunimi || ''
  )
  contactInfoList.unshift(userNames)

  const welcomeTranslation = useTranslation(
    'pages.frontPage.sections.userInfo.welcome'
  )
  const welcomeText = `${welcomeTranslation} ${user?.nimi?.kutsumanimi}`

  const department =
    membership?.jasenyys?.ammattiosasto ||
    membership?.jasenyys?.ammattiosastoTunnus
  const userDepartment = useTextOrTranslation(
    department,
    'pages.frontPage.sections.membership.noDepartment'
  )
  const userMemberNumber = membership?.jasennumero?.toString()
  const userMemberShip = useConditionalText(
    membership?.jasennumero != null,
    'pages.frontPage.sections.membership.member',
    'pages.frontPage.sections.membership.notMember'
  )

  const latestLogin = useLatestLogin(user)

  const validEmployments = employment
    ? filterValidEmployment(employment).map(
        (employment) => employment.employmentText
      )
    : []

  const data: FormattedUserData = {
    contactInfoList,
    latestLogin,
    userDepartment,
    userMemberNumber,
    userMemberShip,
    validEmployments,
    welcomeText
  }

  Object.entries(data).forEach(([key, value]) => {
    // replace values containing null/undefined value or toString of it
    if (
      value == null ||
      !isArrayWithLength(value) ||
      value.toString().toLowerCase().includes('undefined')
    ) {
      const userDataKey = key as FormattedUserDataKey

      if (userDataKey === 'validEmployments') {
        data[userDataKey] = []
      } else if (userDataKey === 'contactInfoList') {
        data[userDataKey] = ['']
      } else {
        data[userDataKey] = ''
      }
    }
  })

  return data
}
